import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { getAllLocations, getLocations } from 'services';
import { Location, Role } from 'types';
import { useAuth } from '.';

type SelectedLocationContextType = {
  selectedLocation: Location | null;
  setSelectedLocation: (location: Location) => void;
  setSelectedLocationById: (locationId: number) => void;
  locations: Location[];
  allLocations: Location[];
};

export const SelectedLocationContext = createContext<SelectedLocationContextType>({
  selectedLocation: null,
  setSelectedLocation: () => {},
  setSelectedLocationById: () => {},
  locations: [],
  allLocations: []
});

export const useSelectedLocation = () => useContext(SelectedLocationContext);

export const SelectedLocationProvider: FC<PropsWithChildren> = ({ children }) => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const [_, setSearchParams] = useSearchParams();

  const [selectedLocation, setSelectedLocation] = useState<Location | null>(userProfile.defaultLocation ?? null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]);

  const handleSetLocations = useCallback(
    (data: Location[]) => setLocations(data.sort((l0, l1) => l0.name.localeCompare(l1.name))),
    [setLocations]
  );

  useEffect(() => {
    if (
      userProfile.role !== Role.BOOKING &&
      userProfile.role !== Role.GATE &&
      userProfile.role !== Role.GATE_BOOKING &&
      userProfile.locations.length === 0
    ) {
      getLocations().then(handleSetLocations);
      getAllLocations().then(setAllLocations);
    } else {
      handleSetLocations(userProfile.locations);
    }
  }, [userProfile, handleSetLocations]);

  useEffect(() => {
    const locationParam = new URLSearchParams(location.search).get('location');
    const newLoc = locations.find(loc => loc.id === Number(locationParam));
    if (!!newLoc && newLoc.id !== selectedLocation?.id) setSelectedLocation(newLoc);
  }, [location, locations, selectedLocation]);

  // Update selected location
  const updateSelectedLocation = useCallback((location: Location) => {
    setSelectedLocation(location);
  }, []);

  // Update selected location by id
  const setSelectedLocationById = useCallback(
    (locationId: number) => {
      setSearchParams(prevState => {
        prevState.set('location', `${locationId}`);
        return prevState;
      });
    },
    [setSearchParams]
  );

  return (
    <SelectedLocationContext.Provider
      value={{
        selectedLocation,
        setSelectedLocation: updateSelectedLocation,
        setSelectedLocationById,
        locations,
        allLocations
      }}
    >
      {children}
    </SelectedLocationContext.Provider>
  );
};
